import React, { useState } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import fr from 'date-fns/locale/fr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './AdminCalendar.css';

const locales = {
    'fr': fr,
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

const AdminCalendar = ({ appointments, blockedDays = [], planningConfig = [], onEventClick, onBlockDate }) => {
    // Controlled state for navigation
    const [view, setView] = useState(Views.MONTH);
    const [date, setDate] = useState(new Date());

    const handleNavigate = (newDate) => setDate(newDate);
    const handleViewChange = (newView) => setView(newView);

    // 1 & 4. Transform appointments with real duration and icons
    const appointmentEvents = appointments.map(apt => {
        const start = new Date(apt.date_heure);
        const durationMin = apt.duree || 90; // Fallback to 1h30
        const end = new Date(start.getTime() + durationMin * 60 * 1000);
        
        const isMobilier = apt.prestation === 'Multi-Prestations' 
            ? apt.options?.includes('Mobilier') 
            : !['Citadine', 'Berline', 'SUV'].some(v => apt.prestation?.includes(v));
        
        const icon = isMobilier ? '🛋️' : '🚗';
        
        return {
            id: apt.id,
            title: `${icon} ${apt.client_nom} - ${apt.prestation}`,
            start,
            end,
            resource: apt,
            status: apt.statut,
            type: 'appointment'
        };
    });

    // 2. Transform blocked dates into background events
    const closureEvents = blockedDays.map(block => ({
        id: `block-${block.id}`,
        title: `🚫 ${block.raison || 'Fermé'}`,
        start: new Date(block.date),
        end: new Date(new Date(block.date).getTime() + 24 * 60 * 60 * 1000), // Full day
        type: 'closure',
        allDay: true
    }));

    // Add Weekly Closures as background events
    const weeklyClosureEvents = [];
    // Generate for a wider range to ensure visibility
    for (let i = -60; i < 90; i++) {
        const d = new Date(date); // Relative to current visible date
        d.setDate(d.getDate() + i);
        const dayName = d.toLocaleDateString('fr-FR', { weekday: 'long' });
        const dayFormatted = dayName.charAt(0).toUpperCase() + dayName.slice(1);
        const config = planningConfig.find(p => p.jour === dayFormatted);
        
        if (config && !config.ouvert) {
            weeklyClosureEvents.push({
                id: `weekly-${i}`,
                title: 'Fermé',
                start: new Date(d.setHours(0,0,0,0)),
                end: new Date(d.setHours(23,59,59,999)),
                type: 'closure',
                allDay: true
            });
        }
    }

    const allEvents = [...appointmentEvents, ...closureEvents, ...weeklyClosureEvents];

    const eventStyleGetter = (event) => {
        if (event.type === 'closure') {
            return {
                style: {
                    backgroundColor: 'rgba(100, 116, 139, 0.15)',
                    color: '#64748b',
                    border: '1px dashed rgba(100, 116, 139, 0.3)',
                    opacity: 0.8,
                    borderRadius: '8px'
                }
            };
        }

        let backgroundColor = '#3b82f6'; 

        switch (event.status) {
            case 'en_attente': backgroundColor = '#fbbf24'; break;
            case 'valide': backgroundColor = '#10b981'; break;
            case 'refuse': backgroundColor = '#ef4444'; break;
            case 'termine': backgroundColor = '#64748b'; break;
            default: backgroundColor = '#3b82f6';
        }

        return {
            style: {
                backgroundColor,
                borderRadius: '8px',
                opacity: 0.9,
                color: 'white',
                border: 'none',
                display: 'block',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                padding: '2px 5px'
            }
        };
    };

    return (
        <div className="admin-calendar-wrapper premium-glass-calendar fade-in">
            <Calendar
                localizer={localizer}
                events={allEvents}
                view={view}
                date={date}
                onView={handleViewChange}
                onNavigate={handleNavigate}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 750 }}
                culture='fr'
                messages={{
                    next: "Suivant",
                    previous: "Précédent",
                    today: "Aujourd'hui",
                    month: "Mois",
                    week: "Semaine",
                    day: "Jour",
                    agenda: "Agenda",
                    date: "Date",
                    time: "Heure",
                    event: "Événement",
                    allDay: "Journée",
                    noEventsInRange: "Aucun rendez-vous ici."
                }}
                eventPropGetter={eventStyleGetter}
                onSelectEvent={onEventClick}
                selectable
                onSelectSlot={({ start }) => {
                    const confirmBlock = window.confirm(`Bloquer le ${start.toLocaleDateString('fr-FR')} ?`);
                    if (confirmBlock) {
                        const reason = window.prompt("Motif ?", "Repos / Congés");
                        if (reason && onBlockDate) onBlockDate(start, reason);
                    }
                }}
                min={new Date(0, 0, 0, 8, 0, 0)}
                max={new Date(0, 0, 0, 20, 0, 0)}
            />
        </div>
    );
};

export default AdminCalendar;
